import { zodResolver } from '@hookform/resolvers/zod';
import { emailSchema } from '@serviceportal/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, LockOpen, LogOut, Mail, MoreHorizontal, Pencil, Search, ShieldCheck, ShieldOff, Trash2, UserPlus, UserX, UserCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Badge, Card, Checkbox, Input, Label, Select, Skeleton } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Paginated, RoleItem, UserItem, UserStatus } from '@/lib/types';
import { formatRelative, initials } from '@/lib/utils';

const STATUS: Record<UserStatus, { label: string; variant: 'success' | 'warning' | 'destructive' }> = {
  ACTIVE: { label: 'Aktiv', variant: 'success' },
  INVITED: { label: 'Eingeladen', variant: 'warning' },
  DISABLED: { label: 'Gesperrt', variant: 'destructive' },
};

const userForm = z.object({
  email: emailSchema,
  firstName: z.string().trim().min(1, 'Pflichtfeld').max(100),
  lastName: z.string().trim().min(1, 'Pflichtfeld').max(100),
  phone: z.string().trim().max(50),
  calendarAddress: z.union([z.literal(''), z.string().trim().email('Ungültige E-Mail-Adresse')]),
  roleIds: z.array(z.string()),
});
type UserForm = z.infer<typeof userForm>;

function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: () => api<RoleItem[]>('/roles') });
}

function RolePicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { data: roles } = useRoles();
  const groups = [
    { audience: 'INTERNAL', label: 'Mitarbeiterrollen' },
    { audience: 'CUSTOMER', label: 'Kundenrollen' },
  ] as const;
  return (
    <div className="flex flex-col gap-4">
      {groups.map((g) => (
        <div key={g.audience}>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{g.label}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {roles
              ?.filter((r) => r.audience === g.audience)
              .map((r) => {
                const checked = value.includes(r.id);
                return (
                  <label key={r.id} className="flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 hover:bg-accent/50">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(c) => onChange(c ? [...value, r.id] : value.filter((id) => id !== r.id))}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm font-medium">{r.name}</span>
                      {r.description && <span className="block text-xs text-muted-foreground">{r.description}</span>}
                    </span>
                  </label>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
}

function UserDialog({ user, open, onOpenChange }: { user: UserItem | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const form = useForm<UserForm>({ resolver: zodResolver(userForm) });

  useEffect(() => {
    if (open) {
      form.reset({
        email: user?.email ?? '',
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        phone: user?.phone ?? '',
        calendarAddress: user?.calendarAddress ?? '',
        roleIds: user?.roles.map((r) => r.id) ?? [],
      });
    }
  }, [open, user, form]);

  const mutation = useMutation({
    mutationFn: (v: UserForm) => {
      const { calendarAddress, ...rest } = v;
      const body = { ...rest, phone: v.phone || null };
      return user
        ? api(`/users/${user.id}`, { method: 'PATCH', body: { ...body, calendarAddress: calendarAddress || null } })
        : api('/users', { method: 'POST', body: { ...body, sendInvitation: true } });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['roles'] });
      toast.success(user ? 'Benutzer gespeichert' : 'Einladung versendet');
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide>
        <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate className="flex min-h-0 flex-col">
          <DialogHeader>
            <DialogTitle>{user ? 'Benutzer bearbeiten' : 'Benutzer einladen'}</DialogTitle>
            <DialogDescription>
              {user ? 'Ändert sich die E-Mail-Adresse, muss sie neu bestätigt werden.' : 'Der Benutzer erhält eine E-Mail, um sein Passwort festzulegen.'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4 sm:grid-cols-2">
            <Field label="Vorname" htmlFor="u-first" error={form.formState.errors.firstName?.message}>
              <Input id="u-first" {...form.register('firstName')} />
            </Field>
            <Field label="Nachname" htmlFor="u-last" error={form.formState.errors.lastName?.message}>
              <Input id="u-last" {...form.register('lastName')} />
            </Field>
            <Field label="E-Mail-Adresse" htmlFor="u-email" error={form.formState.errors.email?.message}>
              <Input id="u-email" type="email" {...form.register('email')} />
            </Field>
            <Field label="Telefon (Handy)" htmlFor="u-phone" hint="Für WhatsApp-Benachrichtigungen">
              <Input id="u-phone" type="tel" {...form.register('phone')} />
            </Field>
            {user && (
              <Field
                label="Outlook-Kalender (optional)"
                htmlFor="u-cal"
                className="sm:col-span-2"
                error={form.formState.errors.calendarAddress?.message}
                hint="Microsoft-365-Postfach für Termine, falls abweichend von der E-Mail-Adresse."
              >
                <Input id="u-cal" type="email" placeholder={user.email} {...form.register('calendarAddress')} />
              </Field>
            )}
            <div className="sm:col-span-2">
              <Label className="mb-2 block">Rollen</Label>
              <Controller control={form.control} name="roleIds" render={({ field }) => <RolePicker value={field.value ?? []} onChange={field.onChange} />} />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {user ? 'Speichern' : 'Einladen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserActions({ user, onEdit }: { user: UserItem; onEdit: () => void }) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const run = useMutation({
    mutationFn: ({ path, body, method }: { path: string; body?: unknown; method?: 'POST' | 'PATCH' | 'DELETE' }) => api(path, { method: method ?? 'POST', body }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      toast.success(v.path.endsWith('invite') ? 'Einladung erneut versendet' : v.method === 'DELETE' ? 'Benutzer gelöscht' : 'Gespeichert');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const [confirmDialog, confirm] = useConfirm();
  const isSelf = me?.user.id === user.id;
  const locked = user.lockedUntil && new Date(user.lockedUntil) > new Date();

  return (
    <>
    {confirmDialog}
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-8" aria-label={`Aktionen für ${user.firstName} ${user.lastName}`}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil /> Bearbeiten
        </DropdownMenuItem>
        {user.status === 'INVITED' && (
          <DropdownMenuItem onSelect={() => run.mutate({ path: `/users/${user.id}/invite` })}>
            <Mail /> Einladung erneut senden
          </DropdownMenuItem>
        )}
        {locked && (
          <DropdownMenuItem onSelect={() => run.mutate({ path: `/users/${user.id}/unlock` })}>
            <LockOpen /> Login-Sperre aufheben
          </DropdownMenuItem>
        )}
        {user.status === 'ACTIVE' && (
          <DropdownMenuItem onSelect={() => run.mutate({ path: `/users/${user.id}/sessions/revoke` })}>
            <LogOut /> Alle Sitzungen beenden
          </DropdownMenuItem>
        )}
        {!isSelf && user.status !== 'INVITED' && (
          <>
            <DropdownMenuSeparator />
            {user.status === 'ACTIVE' ? (
              <DropdownMenuItem
                destructive
                onSelect={async () => {
                  const ok = await confirm({
                    title: `${user.firstName} ${user.lastName} sperren?`,
                    description: 'Der Benutzer wird sofort abgemeldet und kann sich nicht mehr anmelden.',
                    confirmLabel: 'Sperren',
                    destructive: true,
                  });
                  if (ok) run.mutate({ path: `/users/${user.id}`, method: 'PATCH', body: { status: 'DISABLED' } });
                }}
              >
                <UserX /> Konto sperren
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => run.mutate({ path: `/users/${user.id}`, method: 'PATCH', body: { status: 'ACTIVE' } })}>
                <UserCheck /> Konto entsperren
              </DropdownMenuItem>
            )}
            {user.mfaEnabled && (
              <DropdownMenuItem
                onSelect={async () => {
                  const ok = await confirm({
                    title: `Zwei-Faktor-Anmeldung von ${user.firstName} ${user.lastName} zurücksetzen?`,
                    description: 'Zum Beispiel bei verlorenem Telefon. Der Benutzer wird abgemeldet und richtet die Authenticator-App bei der nächsten Anmeldung neu ein.',
                    confirmLabel: 'Zurücksetzen',
                  });
                  if (ok) run.mutate({ path: `/users/${user.id}/2fa/reset` });
                }}
              >
                <ShieldOff /> 2FA zurücksetzen
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              destructive
              onSelect={async () => {
                const ok = await confirm({
                  title: `${user.firstName} ${user.lastName} löschen?`,
                  description: 'Das Konto wird endgültig gelöscht: Name, E-Mail-Adresse und Zugangsdaten werden entfernt. Vorgänge und Verlauf bleiben erhalten (als „Gelöschter Benutzer“). Nur zum vorübergehenden Ausschließen stattdessen „Konto sperren“ verwenden.',
                  confirmLabel: 'Endgültig löschen',
                  destructive: true,
                });
                if (ok) run.mutate({ path: `/users/${user.id}`, method: 'DELETE' });
              }}
            >
              <Trash2 /> Löschen
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
}

export function UsersPage() {
  const { can } = useAuth();
  const canManage = can('admin.users.manage');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [roleId, setRoleId] = useState('');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<{ open: boolean; user: UserItem | null }>({ open: false, user: null });
  const { data: roles } = useRoles();

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const params = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (debounced) params.set('search', debounced);
  if (status) params.set('status', status);
  if (roleId) params.set('roleId', roleId);

  const { data, isLoading } = useQuery({
    queryKey: ['users', params.toString()],
    queryFn: () => api<Paginated<UserItem>>(`/users?${params}`),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Benutzer"
        description="Mitarbeiter und Portalbenutzer Ihrer Organisation"
        actions={
          canManage && (
            <Button onClick={() => setDialog({ open: true, user: null })}>
              <UserPlus /> Benutzer einladen
            </Button>
          )
        }
      />
      <Card>
        <div className="flex flex-col gap-2 border-b p-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Name oder E-Mail suchen" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" aria-label="Suche" />
          </div>
          <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="sm:w-40" aria-label="Status">
            <option value="">Alle Status</option>
            <option value="ACTIVE">Aktiv</option>
            <option value="INVITED">Eingeladen</option>
            <option value="DISABLED">Gesperrt</option>
          </Select>
          <Select value={roleId} onChange={(e) => { setRoleId(e.target.value); setPage(1); }} className="sm:w-56" aria-label="Rolle">
            <option value="">Alle Rollen</option>
            {roles?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </div>
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Name</TH>
              <TH className="hidden md:table-cell">Rollen</TH>
              <TH>Status</TH>
              <TH className="hidden lg:table-cell">Letzte Anmeldung</TH>
              {canManage && <TH className="w-12" />}
            </TR>
          </THead>
          <TBody>
            {isLoading &&
              Array.from({ length: 5 }, (_, i) => (
                <TR key={i}>
                  <TD colSpan={5}>
                    <Skeleton className="h-8" />
                  </TD>
                </TR>
              ))}
            {data?.items.length === 0 && <EmptyRow colSpan={5}>Keine Benutzer gefunden.</EmptyRow>}
            {data?.items.map((u) => {
              const locked = u.lockedUntil && new Date(u.lockedUntil) > new Date();
              return (
                <TR key={u.id}>
                  <TD>
                    <div className="flex items-center gap-3">
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                        {initials(u.firstName, u.lastName)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {u.firstName} {u.lastName}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                  </TD>
                  <TD className="hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.length === 0 && <span className="text-xs text-muted-foreground">keine</span>}
                      {u.roles.map((r) => (
                        <Badge key={r.id} variant={r.audience === 'CUSTOMER' ? 'outline' : 'secondary'}>
                          {r.name}
                        </Badge>
                      ))}
                    </div>
                  </TD>
                  <TD>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant={STATUS[u.status].variant}>{STATUS[u.status].label}</Badge>
                      {locked && (
                        <Badge variant="destructive">
                          <KeyRound className="size-3" /> Login gesperrt
                        </Badge>
                      )}
                      {u.mfaEnabled && (
                        <Badge variant="outline" title="Zwei-Faktor-Anmeldung eingerichtet">
                          <ShieldCheck className="size-3" /> 2FA
                        </Badge>
                      )}
                    </div>
                  </TD>
                  <TD className="hidden text-muted-foreground lg:table-cell">{u.lastLoginAt ? formatRelative(u.lastLoginAt) : 'nie'}</TD>
                  {canManage && (
                    <TD>
                      <UserActions user={u} onEdit={() => setDialog({ open: true, user: u })} />
                    </TD>
                  )}
                </TR>
              );
            })}
          </TBody>
        </Table>
        {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
      </Card>
      {canManage && <UserDialog open={dialog.open} user={dialog.user} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} />}
    </>
  );
}
